import { environment } from "../../config/environment";
import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/Select";
import { Badge } from "../ui/Badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/Dialog";
import { Plus, Trash2, Edit, Download, Route, FileText, Loader2, Search, X, GripVertical } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../ui/Toaster";
import { queryDoctorsBySpecialty, queryAllDoctorsBySpecialties } from "../../utils/functions";
import { customFetch } from "../../utils/Auth";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getPatientTypeLabel,
  getPatientTypeOptionsForRoute,
  shouldScrollPatientTypeList,
} from "./patientTypeOptions";

// Sortable Route Item Component
const SortableRouteItem = ({ route, index, onEdit, onDelete, getTemplateDisplayName, getPatientTypeLabel }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `route-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-2 pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
                title="Drag to reorder"
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </div>
              <CardTitle className="text-base">Route {index + 1}</CardTitle>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onEdit(route, index)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onDelete(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {/* Conditions */}
          <div>
            <h4 className="font-medium text-sm text-gray-700 mb-2">Conditions</h4>
            <div className="space-y-2">
              {/* Patient Types */}
              {route.conditions.patient_types?.length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-gray-600 mb-1">Patient Types</h5>
                  <div className="flex flex-wrap gap-1">
                    {route.conditions.patient_types.map((type, i) => (
                      <Badge key={i} variant="outline" className="w-fit text-xs">
                        {getPatientTypeLabel(type)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Providers */}
              {route.conditions.providers?.length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-gray-600 mb-1">Providers</h5>
                  <div className="flex flex-wrap gap-1">
                    {route.conditions.providers.map((provider, i) => (
                      <Badge key={i} variant="outline" className="w-fit text-xs">
                        {provider.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Flags */}
              {route.conditions.flags?.length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-gray-600 mb-1">Flags</h5>
                  <div className="flex flex-wrap gap-1">
                    {route.conditions.flags.map((flag, i) => (
                      <Badge key={i} variant="outline" className="w-fit text-xs">
                        {flag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-medium text-sm text-gray-700 mb-2">Resources</h4>
            <div className="space-y-2">
              {route.resources.soap_note_template?.id && (
                <div className="flex items-center gap-2">
                  <Badge className="border-blue-200 bg-blue-50 text-blue-700 text-xs">
                    SOAP Template
                  </Badge>
                  <span className="text-sm">{getTemplateDisplayName(route.resources.soap_note_template.id)}</span>
                </div>
              )}
              {route.resources.conversation_prompt?.id && (
                <div className="flex items-center gap-2">
                  <Badge className="border-green-200 bg-green-50 text-green-700 text-xs">
                    Conversation Prompt
                  </Badge>
                  <span className="text-sm">{getTemplateDisplayName(route.resources.conversation_prompt.id)}</span>
                </div>
              )}
              {route.resources.secondary_prompt?.id && (
                <div className="flex items-center gap-2">
                  <Badge className="border-purple-200 bg-purple-50 text-purple-700 text-xs">
                    Secondary Prompt
                  </Badge>
                  <span className="text-sm">{getTemplateDisplayName(route.resources.secondary_prompt?.id)}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Simple Template Search Component (based on doctor search pattern)
const TemplateSearch = ({ 
  value, 
  onValueChange, 
  searchTerm, 
  onSearchChange, 
  placeholder, 
  label,
  className = "",
  templates,
  getFilteredTemplateOptions,
  type
}) => {
  // Filter templates by type if specified
  const templatesToSearch = type ? templates.filter(t => t.type === type) : templates;
  const filteredTemplates = getFilteredTemplateOptions(templatesToSearch, searchTerm);
  
  // Deduplicate templates by name to show only unique values in dropdown
  const uniqueTemplates = filteredTemplates.filter((template, index, self) =>
    index === self.findIndex(t => t.name === template.name)
  );
  
  const selectedTemplate = templates.find(t => t.name === value);

  return (
    <div className={`w-full ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      
      {/* Show selected template as chip or input field */}
      {value && value !== "none" ? (
        <div className="flex items-center gap-2 mb-2">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${
            type === "SOAP_NOTE_TEMPLATE" 
              ? "border-blue-200 bg-blue-50 text-blue-700"
              : type === "CONVERSATION_PROMPT"
              ? "border-green-200 bg-green-50 text-green-700"
              : type === "SECONDARY_PROMPT"
              ? "border-purple-200 bg-purple-50 text-purple-700"
              : "bg-purple-100 text-purple-800"
          }`}>
            <span className="text-sm font-medium">{value}</span>
            <button
              type="button"
              onClick={() => onValueChange("none")}
              className={`focus:outline-none ${
                type === "SOAP_NOTE_TEMPLATE" 
                  ? "text-blue-600 hover:text-blue-800"
                  : type === "CONVERSATION_PROMPT"
                  ? "text-green-600 hover:text-green-800"
                  : type === "SECONDARY_PROMPT"
                  ? "text-purple-600 hover:text-purple-800"
                  : "text-purple-600 hover:text-purple-800"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
      
      {/* Always show search input */}
      <input
        key={`template-search-${label}`}
        type="text"
        autoComplete="off"
        value={searchTerm}
        onChange={e => {
          onSearchChange(e.target.value);
          // Clear current selection when user starts typing (but not when clearing search)
          if (e.target.value && value && value !== "none") {
            onValueChange("none");
          }
        }}
        placeholder={value && value !== "none" ? "Search for a different template..." : placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple mb-2"
      />
      
      {searchTerm && uniqueTemplates.length > 0 && (
        <div className="w-full max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow z-10">
          {uniqueTemplates.map(template => (
            <div
              key={template.name}
              className={`px-4 py-2 cursor-pointer hover:bg-purple-100 ${value === template.name ? 'bg-purple-200' : ''}`}
              onClick={() => {
                onValueChange(template.name);
                onSearchChange("");
              }}
            >
              <div className="font-semibold text-gray-900">
                {template.name}
              </div>
            </div>
          ))}
        </div>
      )}
      {searchTerm && uniqueTemplates.length === 0 && (
        <div className="w-full px-4 py-2 text-gray-400">No templates found.</div>
      )}
    </div>
  );
};

const Workflows = ({ setAuth, setUserOrg }) => {
  const { toast } = useToast();
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredWorkflows, setFilteredWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [specialtyDialogOpen, setSpecialtyDialogOpen] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalWorkflow, setOriginalWorkflow] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [templates, setTemplates] = useState([]);
  const [modelEditForm, setModelEditForm] = useState({
    platform: "",
    model: ""
  });

  
  // Search states for templates
  const [soapSearchTerm, setSoapSearchTerm] = useState("");
  const [convSearchTerm, setConvSearchTerm] = useState("");
  const [secondarySearchTerm, setSecondarySearchTerm] = useState("");
  const [defaultSoapSearchTerm, setDefaultSoapSearchTerm] = useState("");
  const [defaultConvSearchTerm, setDefaultConvSearchTerm] = useState("");
  const [defaultSecondarySearchTerm, setDefaultSecondarySearchTerm] = useState("");

  // Provider search states
  const [providerSearchTerm, setProviderSearchTerm] = useState("");
  const [availableProviders, setAvailableProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [allDoctorsBySpecialties, setAllDoctorsBySpecialties] = useState({});



  const [routeForm, setRouteForm] = useState({
    conditions: {
      providers: [],
      patient_types: [],
      flags: []
    },
    resources: {
      soap_note_template: { id: "none" },
      conversation_prompt: { id: "none" },
      // secondary_prompt is optional; omit when not set
    }
  });

  // Available options
  const modelPlatforms = ["openai", "anthropic", "google", "knowtex"];
  const openaiModels = ["gpt-4o-mini", "gpt-4o", "gpt-4-turbo"];
  const anthropicModels = ["claude-sonnet-4-6", "claude-3-7-sonnet-20250219", "claude-sonnet-4-5-20250929", "claude-3-5-sonnet", "claude-3-opus", "claude-3-haiku"];
  const googleModels = ["gemini-1.5-pro", "gemini-1.5-flash"];
  const knowtexModels = ["mock"];

  const patientTypeOptions = getPatientTypeOptionsForRoute(
    selectedWorkflow?.specialty,
    routeForm.conditions.patient_types
  );
  const availableFlags = [
    "is_first_note_present", "is_aesthetic", "is_labs", "is_patient_surgery", 
    "is_ob", "is_op", "is_generic", "is_jaw", "is_pathology", 
    "is_tmj", "is_d12", "is_arm_body_part", "is_lower_body_part", "is_prechart"
  ];

  // Ensure each route has conditions.patient_types as an array and no secondary_prompt with id="none"
  const normalizeRoute = (route) => {
    const normalizedRoute = { ...route };
    normalizedRoute.conditions = (normalizedRoute.conditions && typeof normalizedRoute.conditions === 'object')
      ? { ...normalizedRoute.conditions }
      : {};
    if (!Array.isArray(normalizedRoute.conditions.patient_types)) {
      normalizedRoute.conditions.patient_types = [];
    }
    // Normalize resources
    normalizedRoute.resources = (normalizedRoute.resources && typeof normalizedRoute.resources === 'object')
      ? { ...normalizedRoute.resources }
      : {};
    if (!normalizedRoute.resources.secondary_prompt || normalizedRoute.resources.secondary_prompt.id === "none" || !normalizedRoute.resources.secondary_prompt.id) {
      delete normalizedRoute.resources.secondary_prompt;
    }
    return normalizedRoute;
  };

  const normalizeWorkflow = (workflow) => {
    const normalizedWorkflow = { ...workflow };
    if (Array.isArray(normalizedWorkflow.routes)) {
      normalizedWorkflow.routes = normalizedWorkflow.routes.map(normalizeRoute);
    } else {
      normalizedWorkflow.routes = [];
    }
    // Normalize default route secondary_prompt
    if (
      normalizedWorkflow.default_route &&
      normalizedWorkflow.default_route.resources &&
      typeof normalizedWorkflow.default_route.resources === 'object'
    ) {
      const dr = normalizedWorkflow.default_route.resources;
      if (!dr.secondary_prompt || dr.secondary_prompt.id === "none" || !dr.secondary_prompt.id) {
        delete dr.secondary_prompt;
      }
    }
    return normalizedWorkflow;
  };

  useEffect(() => {
    fetchTemplates();
    loadWorkflowsFromS3();
    fetchAllDoctorsBySpecialties();
  }, []);

  const fetchAllDoctorsBySpecialties = async () => {
    try {
      const doctorsBySpecialties = await queryAllDoctorsBySpecialties();
      setAllDoctorsBySpecialties(doctorsBySpecialties);
    } catch (error) {
      console.error("Error fetching all doctors by specialties:", error);
      setAllDoctorsBySpecialties({});
    }
  };

  // Filter workflows based on search query
  useEffect(() => {
    let filtered = workflows;
    
    if (searchQuery) {
      filtered = filtered.filter(workflow => 
        workflow.specialty.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredWorkflows(filtered);
  }, [workflows, searchQuery]);

  // Separate workflows with and without providers
  const [workflowsWithProviders, setWorkflowsWithProviders] = useState([]);
  const [workflowsWithoutProviders, setWorkflowsWithoutProviders] = useState([]);

  useEffect(() => {
    const separateWorkflowsByProviders = () => {
      const withProviders = [];
      const withoutProviders = [];

      filteredWorkflows.forEach(workflow => {
        const providers = allDoctorsBySpecialties[workflow.specialty];
        if (providers && providers.length > 0) {
          withProviders.push(workflow);
        } else {
          withoutProviders.push(workflow);
        }
      });

      // Sort workflows with providers by last updated timestamp (most recent first), then alphabetically
      withProviders.sort((a, b) => {
        // If both have history, sort by timestamp (most recent first)
        if (a.history && b.history) {
          const timestampA = new Date(a.history.timestamp).getTime();
          const timestampB = new Date(b.history.timestamp).getTime();
          return timestampB - timestampA; // Most recent first
        }
        // If only one has history, prioritize it
        if (a.history && !b.history) return -1;
        if (!a.history && b.history) return 1;
        // If neither has history, sort alphabetically
        return a.specialty.localeCompare(b.specialty);
      });

      // Sort workflows without providers by last updated timestamp (most recent first), then alphabetically
      withoutProviders.sort((a, b) => {
        // If both have history, sort by timestamp (most recent first)
        if (a.history && b.history) {
          const timestampA = new Date(a.history.timestamp).getTime();
          const timestampB = new Date(b.history.timestamp).getTime();
          return timestampB - timestampA; // Most recent first
        }
        // If only one has history, prioritize it
        if (a.history && !b.history) return -1;
        if (!a.history && b.history) return 1;
        // If neither has history, sort alphabetically
        return a.specialty.localeCompare(b.specialty);
      });

      setWorkflowsWithProviders(withProviders);
      setWorkflowsWithoutProviders(withoutProviders);
    };

    if (filteredWorkflows.length > 0 && Object.keys(allDoctorsBySpecialties).length > 0) {
      separateWorkflowsByProviders();
    } else {
      setWorkflowsWithProviders([]);
      setWorkflowsWithoutProviders([]);
    }
  }, [filteredWorkflows, allDoctorsBySpecialties]);

  // Filter providers based on search query
  useEffect(() => {
    if (providerSearchTerm.trim()) {
      const search = providerSearchTerm.trim().toLowerCase();
      setFilteredProviders(
        availableProviders.filter(provider => {
          const name = provider.user_name?.toLowerCase() || "";
          const email = provider.user_email?.toLowerCase() || "";
          return name.includes(search) || email.includes(search);
        })
      );
    } else {
      setFilteredProviders(availableProviders);
    }
  }, [providerSearchTerm, availableProviders]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await customFetch(`${environment.apiUrl}/templates/get-all-templates`, {
        headers: { token }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const loadWorkflowsFromS3 = useCallback(async () => {
    try {
      setLoadingWorkflows(true);
      const token = localStorage.getItem("token");
      if (!token) {
        setLoadingWorkflows(false);
        return;
      }

      const response = await customFetch(`${environment.apiUrl}/workflows/load-from-s3`, {
        headers: { token }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.workflows) {
          const normalized = result.data.workflows.map(normalizeWorkflow);
          setWorkflows(normalized);
        }
      } else {
        console.error("Failed to load workflows from S3");
      }
    } catch (error) {
      console.error("Error loading workflows from S3:", error);
    } finally {
      setLoadingWorkflows(false);
    }
  }, [toast]);

  const getModelsByPlatform = (platform) => {
    switch (platform) {
      case "openai": return openaiModels;
      case "anthropic": return anthropicModels;
      case "google": return googleModels;
      case "knowtex": return knowtexModels;
      case "none": return [];
      default: return [];
    }
  };





  const checkForChanges = (currentWorkflow) => {
    if (!originalWorkflow) return false;
    
    // Deep comparison of the workflows
    return JSON.stringify(currentWorkflow) !== JSON.stringify(originalWorkflow);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const updateWorkflowWithChangeTracking = (updatedWorkflow) => {
    setWorkflows(workflows.map(w => 
      w.specialty === selectedWorkflow.specialty ? updatedWorkflow : w
    ));
    setSelectedWorkflow(updatedWorkflow);
    setHasUnsavedChanges(checkForChanges(updatedWorkflow));
  };

  const getCurrentUsername = () => {
    return localStorage.getItem("username") || "unknown";
  };

  const saveWorkflowChanges = async () => {
    try {
      // Update the history before saving
      const updatedWorkflow = {
        ...selectedWorkflow,
        history: {
          user: getCurrentUsername(),
          timestamp: new Date().toISOString()
        }
      };
      
      // Create the updated workflows array
      const updatedWorkflows = workflows.map(w => 
        w.specialty === selectedWorkflow.specialty ? updatedWorkflow : w
      );
      
      // Update the workflows state with the new history
      setWorkflows(updatedWorkflows);
      setSelectedWorkflow(updatedWorkflow);
      
      // Save with the updated workflows (pass directly to avoid stale state)
      await saveWorkflowsToS3(updatedWorkflows);
      setHasUnsavedChanges(false);
      setOriginalWorkflow(JSON.parse(JSON.stringify(updatedWorkflow))); // Update original to current state
      toast({
        title: "Success",
        description: "Workflow changes saved successfully",
      });
    } catch (error) {
      console.error("Error saving workflow changes:", error);
      toast({
        title: "Error",
        description: "Failed to save workflow changes",
        variant: "destructive",
      });
    }
  };

  const openSpecialtyDialog = (workflow) => {
    setSelectedWorkflow(workflow);
    setOriginalWorkflow(JSON.parse(JSON.stringify(workflow))); // Deep copy for comparison
    setHasUnsavedChanges(false);
    setModelEditForm({
      platform: workflow.model.platform || "none",
      model: workflow.model.model || "none"
    });
    setSpecialtyDialogOpen(true);
    
    // Reset provider search state
    setProviderSearchTerm("");
    setAvailableProviders([]);
    setFilteredProviders([]);
    
    // Fetch providers for this specialty
    fetchProvidersForSpecialty(workflow.specialty);
  };

  const fetchProvidersForSpecialty = (specialty) => {
    const providers = allDoctorsBySpecialties[specialty] || [];
    setAvailableProviders(providers);
    setFilteredProviders(providers);
  };

  const closeSpecialtyDialog = () => {
    setSpecialtyDialogOpen(false);
    setHasUnsavedChanges(false);
    setOriginalWorkflow(null);
    // Reset provider search state when closing dialog
    setProviderSearchTerm("");
    setAvailableProviders([]);
    setFilteredProviders([]);
  };

  const resetRouteFormState = () => {
    setRouteForm({
      conditions: {
        providers: [],
        patient_types: [],
        flags: []
      },
      resources: {
        soap_note_template: { id: "none" },
        conversation_prompt: { id: "none" },
        // secondary_prompt omitted when not set
      }
    });
    // Clear all search terms
    setSoapSearchTerm("");
    setConvSearchTerm("");
    setSecondarySearchTerm("");
    setProviderSearchTerm("");
    setEditingRoute(null);
  };

  const closeRouteDialog = () => {
    setRouteDialogOpen(false);
    resetRouteFormState();
  };









  const createNewRoute = () => {
    if (!selectedWorkflow) {
      console.error("No workflow selected");
      return;
    }
    
    resetRouteFormState();
    setRouteDialogOpen(true);
  };

  const editRoute = (route, index) => {
    setEditingRoute({ route, index });
    setRouteForm(normalizeRoute(route));
    // Clear search terms
    setSoapSearchTerm("");
    setConvSearchTerm("");
    setSecondarySearchTerm("");
    setProviderSearchTerm("");
    setRouteDialogOpen(true);
  };

  const saveRoute = () => {
    if (!selectedWorkflow) return;

    // Validate that both SOAP template and conversation prompt are selected and valid
    const soapTemplateId = routeForm.resources.soap_note_template.id;
    const conversationPromptId = routeForm.resources.conversation_prompt.id;
    
    if (!soapTemplateId || soapTemplateId.trim() === "" || soapTemplateId === "none" || 
        !conversationPromptId || conversationPromptId.trim() === "" || conversationPromptId === "none") {
      toast({
        title: "Missing Required Fields",
        description: "Please select both a valid SOAP template and conversation prompt before saving the route.",
        variant: "destructive"
      });
      return;
    }

    const updatedWorkflow = { ...selectedWorkflow };
    
    if (editingRoute) {
      // Update existing route
      updatedWorkflow.routes[editingRoute.index] = routeForm;
    } else {
      // Add new route
      updatedWorkflow.routes.push(routeForm);
    }

    updateWorkflowWithChangeTracking(updatedWorkflow);
    closeRouteDialog();
  };

  const deleteRoute = (index) => {
    if (!selectedWorkflow) {
      console.error("No workflow selected");
      return;
    }

    const updatedWorkflow = { ...selectedWorkflow };
    updatedWorkflow.routes.splice(index, 1);
    
    updateWorkflowWithChangeTracking(updatedWorkflow);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!selectedWorkflow || !over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(active.id.replace('route-', ''));
    const newIndex = parseInt(over.id.replace('route-', ''));

    const updatedWorkflow = { ...selectedWorkflow };
    updatedWorkflow.routes = arrayMove(updatedWorkflow.routes, oldIndex, newIndex);
    
    updateWorkflowWithChangeTracking(updatedWorkflow);
  };

  const updateDefaultRoute = (resourceType, field, value) => {
    if (!selectedWorkflow) {
      console.error("No workflow selected");
      return;
    }

    const updatedWorkflow = { ...selectedWorkflow };
    
    // Ensure the resource type exists in default_route.resources
    if (!updatedWorkflow.default_route.resources[resourceType]) {
      updatedWorkflow.default_route.resources[resourceType] = {};
    }
    
    // If clearing or setting 'none' for secondary_prompt, delete it entirely
    if (resourceType === 'secondary_prompt' && (value === 'none' || !value)) {
      delete updatedWorkflow.default_route.resources.secondary_prompt;
    } else {
      updatedWorkflow.default_route.resources[resourceType][field] = value;
    }
    
    updateWorkflowWithChangeTracking(updatedWorkflow);
  };

  const generateWorkflowJSON = (workflowsInput = null) => {
    const source = Array.isArray(workflowsInput) ? workflowsInput : workflows;
    const workflowData = { workflows: [] };
    
    source.forEach(workflow => {
      // Clean the workflow data by removing "none" and "all" values
      const cleanWorkflow = {
        specialty: workflow.specialty,
        model: workflow.model,
        routes: workflow.routes.map(route => ({
          conditions: {
            providers: route.conditions.providers,
            patient_types: Array.isArray(route.conditions.patient_types) ? route.conditions.patient_types : [],
            flags: route.conditions.flags
          },
          resources: {
            soap_note_template: route.resources.soap_note_template.id === "none" ? undefined : route.resources.soap_note_template,
            conversation_prompt: route.resources.conversation_prompt.id === "none" ? undefined : route.resources.conversation_prompt,
            secondary_prompt: route.resources.secondary_prompt && route.resources.secondary_prompt.id && route.resources.secondary_prompt.id !== "none"
              ? route.resources.secondary_prompt
              : undefined
          }
        })),
        default_route: {
          resources: {
            soap_note_template: workflow.default_route.resources.soap_note_template.id === "none" ? undefined : workflow.default_route.resources.soap_note_template,
            conversation_prompt: workflow.default_route.resources.conversation_prompt.id === "none" ? undefined : workflow.default_route.resources.conversation_prompt,
            secondary_prompt: workflow.default_route.resources.secondary_prompt && workflow.default_route.resources.secondary_prompt.id && workflow.default_route.resources.secondary_prompt.id !== "none"
              ? workflow.default_route.resources.secondary_prompt
              : undefined
          }
        }
      };
      
      // Only include history if it exists
      if (workflow.history) {
        cleanWorkflow.history = workflow.history;
      }
      
      workflowData.workflows.push(cleanWorkflow);
    });

    return workflowData;
  };

  const downloadWorkflowJSON = () => {
    const dataStr = JSON.stringify(generateWorkflowJSON(), null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'workflows.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const saveWorkflowsToS3 = async (workflowsToSave = null) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "Please log in to save workflows",
        });
        return;
      }

      // Always use cleaned JSON derived from provided data when available
      const workflowData = generateWorkflowJSON(workflowsToSave);
      
      const response = await customFetch(`${environment.apiUrl}/workflows/save-to-s3`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token
        },
        body: JSON.stringify(workflowData)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Workflows saved successfully to S3!",
        });
      } else {
        const errorData = await response.json();
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to save workflows: ${errorData.message || "Unknown error"}`,
        });
      }
    } catch (error) {
      console.error("Error saving workflows:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save workflows. Please try again.",
      });
    }
  };



  const getFilteredTemplateOptions = (templates, searchTerm) => {
    if (!searchTerm) return templates;
    return templates.filter(template => 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.name.toLowerCase().split(' ').some(word => word.includes(searchTerm.toLowerCase())) ||
      searchTerm.toLowerCase().split(' ').every(term => template.name.toLowerCase().includes(term))
    );
  };

  const getTemplateDisplayName = (id) => {
    if (id === "none" || !id) return "None";
    const template = templates.find(t => t.name === id);
    return template ? template.name : id;
  };



  return (
    <div className="w-screen h-screen">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div
        className="px-8 flex items-start flex-col pt-8"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="w-full max-w-6xl space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Workflows</h1>
              <p className="text-gray-600 mt-2">
                Build and manage automated workflows with conditional routing
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setJsonDialogOpen(true)} variant="outline">
                Show JSON
              </Button>
            </div>
          </div>

          {/* Search Section */}
          <Card>
                  <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Search className="h-4 w-4" />
                Search Workflows
                        </CardTitle>
                  </CardHeader>
                  <CardContent>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Input
                    placeholder="Search workflows by specialty..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pr-10"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                      </div>
                      </div>
              {searchQuery && (
                <div className="mt-3 text-sm text-gray-600">
                  Found {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? 's' : ''}
                    </div>
              )}
                  </CardContent>
                </Card>



          {/* Loading State */}
          {loadingWorkflows && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ktx-purple mb-4"></div>
                  <p className="text-gray-600">Loading workflows...</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Workflows List */}
          {!loadingWorkflows && (workflowsWithProviders.length > 0 || workflowsWithoutProviders.length > 0) && (
            <div className="space-y-6">
              {/* Workflows with Providers */}
              {workflowsWithProviders.length > 0 && (
                <div className="space-y-4">
                  {workflowsWithProviders.map((workflow) => (
                    <Card 
                      key={workflow.specialty} 
                      className="cursor-pointer transition-all hover:shadow-md"
                      onClick={() => openSpecialtyDialog(workflow)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">
                            {workflow.specialty}
                          </CardTitle>
                          <div className="text-right">
                            <div className="text-sm font-normal text-gray-500">
                              {allDoctorsBySpecialties[workflow.specialty]?.length || 0} {(allDoctorsBySpecialties[workflow.specialty]?.length || 0) === 1 ? 'provider' : 'providers'}
                            </div>
                            {workflow.history && (
                              <div className="mt-1 text-xs text-gray-400 text-right">
                                <span className="font-bold text-gray-600">{workflow.history.user}</span> • {formatTimestamp(workflow.history.timestamp)}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              )}

              {/* Specialties with no providers */}
              {workflowsWithoutProviders.length > 0 && (
                <div className="space-y-4">
                  <div className="border-t pt-6 pb-8">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">
                      Specialties with no providers
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {workflowsWithoutProviders.map((workflow, index) => (
                        <span key={workflow.specialty} className="text-gray-600">
                          {workflow.specialty}
                          {index < workflowsWithoutProviders.length - 1 && <span className="text-gray-400">, </span>}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No Workflows Found */}
          {!loadingWorkflows && workflowsWithProviders.length === 0 && workflowsWithoutProviders.length === 0 && workflows.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                  <Plus className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Workflows Yet
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Create your first workflow to get started with automated routing and resource management.
                </p>
              </CardContent>
            </Card>
          )}

          {/* No Search Results */}
          {!loadingWorkflows && workflowsWithProviders.length === 0 && workflowsWithoutProviders.length === 0 && workflows.length > 0 && searchQuery && (
            <Card>
              <CardContent className="text-center py-12">
                <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  No workflows found matching your search.
                </p>
              </CardContent>
            </Card>
          )}





          {/* Route Dialog */}
          <Dialog open={routeDialogOpen} onOpenChange={(open) => {
            if (!open) {
              closeRouteDialog();
            }
          }}>
            <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingRoute ? 'Edit Route' : 'Create New Route'}
                </DialogTitle>
                <DialogDescription>
                  Configure conditions and resources for this route
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                {/* Conditions */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Conditions</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Patient Types
                      </label>
                      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 border rounded-md p-4 ${shouldScrollPatientTypeList(selectedWorkflow?.specialty) ? "max-h-80 overflow-y-auto" : ""}`}>
                          {patientTypeOptions.map(({ value, label }) => (
                          <label key={value} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={routeForm.conditions.patient_types.includes(value)}
                              onChange={(e) => {
                                const newPatientTypes = e.target.checked
                                  ? [...routeForm.conditions.patient_types, value]
                                  : routeForm.conditions.patient_types.filter(t => t !== value);
                                setRouteForm({
                                  ...routeForm,
                                  conditions: { ...routeForm.conditions, patient_types: newPatientTypes }
                                });
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Flags
                      </label>
                      <div className="grid grid-cols-2 gap-3 border rounded-md p-4">
                        {availableFlags.map((flag) => (
                          <label key={flag} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={routeForm.conditions.flags.includes(flag)}
                              onChange={(e) => {
                                const newFlags = e.target.checked
                                  ? [...routeForm.conditions.flags, flag]
                                  : routeForm.conditions.flags.filter(f => f !== flag);
                                setRouteForm({
                                  ...routeForm,
                                  conditions: { ...routeForm.conditions, flags: newFlags }
                                });
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700">{flag}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Providers
                      </label>
                      <div className="border rounded-md p-4">
                        {/* Provider Search */}
                        <div className="mb-4">
                          <input
                            type="text"
                            value={providerSearchTerm}
                            onChange={(e) => setProviderSearchTerm(e.target.value)}
                            placeholder="Search providers..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple"
                          />
                        </div>

                        {/* Provider List */}
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {filteredProviders.length > 0 ? (
                            filteredProviders.map((provider) => (
                              <label key={provider.user_id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                <input
                                  type="checkbox"
                                  checked={routeForm.conditions.providers.some(p => p.id === provider.user_id)}
                                  onChange={(e) => {
                                    const newProviders = e.target.checked
                                      ? [...routeForm.conditions.providers, { id: provider.user_id, name: provider.user_name }]
                                      : routeForm.conditions.providers.filter(p => p.id !== provider.user_id);
                                    setRouteForm({
                                      ...routeForm,
                                      conditions: { ...routeForm.conditions, providers: newProviders }
                                    });
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{provider.user_name}</div>
                                  <div className="text-xs text-gray-500">{provider.user_email}</div>
                                </div>
                              </label>
                            ))
                          ) : (
                            <div className="text-sm text-gray-500 text-center py-4">
                              {providerSearchTerm ? "No providers found" : "No providers available for this specialty"}
                            </div>
                          )}
                        </div>

                        {/* Selected Providers */}
                        {routeForm.conditions.providers.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="text-sm font-medium text-gray-700 mb-2">Selected Providers:</div>
                            <div className="flex flex-wrap gap-2">
                              {routeForm.conditions.providers.map((provider) => (
                                <Badge key={provider.id} variant="secondary" className="text-xs">
                                  {provider.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resources */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Resources</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <TemplateSearch
                        value={routeForm.resources.soap_note_template.id}
                      onValueChange={(value) => {
                        setRouteForm({
                          ...routeForm,
                          resources: { 
                            ...routeForm.resources, 
                            soap_note_template: {
                              id: value
                            }
                          }
                        });
                      }}
                      searchTerm={soapSearchTerm}
                      onSearchChange={setSoapSearchTerm}
                      placeholder="Select SOAP template"
                      label="SOAP Note Template *"
                      templates={templates}
                      getFilteredTemplateOptions={getFilteredTemplateOptions}
                      type="SOAP_NOTE_TEMPLATE"
                    />

                    <TemplateSearch
                        value={routeForm.resources.conversation_prompt.id}
                      onValueChange={(value) => {
                        setRouteForm({
                            ...routeForm,
                            resources: { 
                              ...routeForm.resources, 
                            conversation_prompt: {
                              id: value
                            }
                          }
                        });
                      }}
                      searchTerm={convSearchTerm}
                      onSearchChange={setConvSearchTerm}
                      placeholder="Select conversation prompt"
                      label="Conversation Prompt *"
                      templates={templates}
                      getFilteredTemplateOptions={getFilteredTemplateOptions}
                      type="CONVERSATION_PROMPT"
                    />

                    <TemplateSearch
                        value={routeForm.resources.secondary_prompt?.id || "none"}
                      onValueChange={(value) => {
                        if (!value || value === "none") {
                          const newResources = { ...routeForm.resources };
                          delete newResources.secondary_prompt;
                          setRouteForm({ ...routeForm, resources: newResources });
                        } else {
                          setRouteForm({
                            ...routeForm,
                            resources: { 
                              ...routeForm.resources,
                              secondary_prompt: { id: value }
                            }
                          });
                        }
                      }}
                      searchTerm={secondarySearchTerm}
                      onSearchChange={setSecondarySearchTerm}
                      placeholder="Select secondary prompt"
                      label="Secondary Prompt"
                      templates={templates}
                      getFilteredTemplateOptions={getFilteredTemplateOptions}
                      type="SECONDARY_PROMPT"
                    />
                    </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeRouteDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={saveRoute}
                  disabled={
                    !routeForm.resources.soap_note_template.id || 
                    routeForm.resources.soap_note_template.id.trim() === "" ||
                    routeForm.resources.soap_note_template.id === "none" ||
                    !routeForm.resources.conversation_prompt.id || 
                    routeForm.resources.conversation_prompt.id.trim() === "" ||
                    routeForm.resources.conversation_prompt.id === "none"
                  }
                >
                  {editingRoute ? 'Update' : 'Create'} Route
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Specialty Dialog */}
          <Dialog open={specialtyDialogOpen} onOpenChange={(open) => {
            if (!open) {
              closeSpecialtyDialog();
            }
          }}>
            <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
                            <DialogHeader>
                <DialogTitle>
                  {selectedWorkflow?.specialty} Workflow
                </DialogTitle>
                <DialogDescription>
                  Manage routes and resources for this workflow
                </DialogDescription>
                {selectedWorkflow?.history && (
                  <div className="mt-2 text-xs text-gray-400">
                    <span className="font-bold text-gray-600">{selectedWorkflow.history.user}</span> • {formatTimestamp(selectedWorkflow.history.timestamp)}
                  </div>
                )}
              </DialogHeader>
              <div className="space-y-6">
                {/* Model Configuration Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Model Configuration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Model Platform
                      </label>
                      <Select
                        value={modelEditForm.platform}
                        onValueChange={(value) => {
                          const newForm = {
                            ...modelEditForm, 
                            platform: value, 
                            model: getModelsByPlatform(value)[0] || 'none'
                          };
                          setModelEditForm(newForm);
                          // Auto-save the changes
                          if (selectedWorkflow) {
                            const updatedWorkflow = {
                              ...selectedWorkflow,
                              model: { 
                                platform: value === "none" ? "" : value,
                                model: (getModelsByPlatform(value)[0] || 'none') === "none" ? "" : (getModelsByPlatform(value)[0] || 'none')
                              }
                            };
                            updateWorkflowWithChangeTracking(updatedWorkflow);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                                                 <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {modelPlatforms.map((platform) => (
                            <SelectItem key={platform} value={platform}>
                              {platform.charAt(0).toUpperCase() + platform.slice(1)}
                             </SelectItem>
                           ))}
                         </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Model
                      </label>
                        <Select
                        value={modelEditForm.model}
                        onValueChange={(value) => {
                          const newForm = {
                            ...modelEditForm, 
                            model: value
                          };
                          setModelEditForm(newForm);
                          // Auto-save the changes
                          if (selectedWorkflow) {
                            const updatedWorkflow = {
                              ...selectedWorkflow,
                              model: { 
                                platform: modelEditForm.platform === "none" ? "" : modelEditForm.platform,
                                model: value === "none" ? "" : value
                              }
                            };
                            updateWorkflowWithChangeTracking(updatedWorkflow);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {getModelsByPlatform(modelEditForm.platform).map((model) => (
                            <SelectItem key={model} value={model}>
                              {model}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                    </div>
                  </div>
                </div>

                {/* Routes Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Conditional Routes</h3>
                    <Button 
                      type="button"
                      size="sm" 
                      onClick={() => {
                        setEditingRoute(null);
                        setRouteDialogOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Route
                    </Button>
                    </div>
                  
                  {selectedWorkflow?.routes.length > 0 ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={selectedWorkflow.routes.map((_, index) => `route-${index}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-4">
                          {selectedWorkflow.routes.map((route, index) => (
                            <SortableRouteItem
                              key={`route-${index}`}
                              route={route}
                              index={index}
                              onEdit={editRoute}
                              onDelete={deleteRoute}
                              getTemplateDisplayName={getTemplateDisplayName}
                              getPatientTypeLabel={(type) => getPatientTypeLabel(type, selectedWorkflow?.specialty)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No routes configured for this workflow
                    </div>
                      )}
                    </div>

                {/* Default Route */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Default Route</h3>
                  <Card className="border-l-4 border-l-gray-500">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TemplateSearch
                          value={selectedWorkflow?.default_route.resources.soap_note_template.id}
                          onValueChange={(value) => {
                            updateDefaultRoute('soap_note_template', 'id', value);
                          }}
                          searchTerm={defaultSoapSearchTerm}
                          onSearchChange={setDefaultSoapSearchTerm}
                          placeholder="Select SOAP template"
                          label="SOAP Note Template"
                          templates={templates}
                          getFilteredTemplateOptions={getFilteredTemplateOptions}
                          type="SOAP_NOTE_TEMPLATE"
                        />

                        <TemplateSearch
                          value={selectedWorkflow?.default_route.resources.conversation_prompt.id}
                          onValueChange={(value) => {
                            updateDefaultRoute('conversation_prompt', 'id', value);
                          }}
                          searchTerm={defaultConvSearchTerm}
                          onSearchChange={setDefaultConvSearchTerm}
                          placeholder="Select conversation prompt"
                          label="Conversation Prompt"
                          templates={templates}
                          getFilteredTemplateOptions={getFilteredTemplateOptions}
                          type="CONVERSATION_PROMPT"
                        />

                        <TemplateSearch
                          value={selectedWorkflow?.default_route.resources.secondary_prompt?.id}
                          onValueChange={(value) => {
                            updateDefaultRoute('secondary_prompt', 'id', value);
                          }}
                          searchTerm={defaultSecondarySearchTerm}
                          onSearchChange={setDefaultSecondarySearchTerm}
                          placeholder="Select secondary prompt"
                          label="Secondary Prompt"
                          templates={templates}
                          getFilteredTemplateOptions={getFilteredTemplateOptions}
                          type="SECONDARY_PROMPT"
                        />
                  </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeSpecialtyDialog}>
                  Close
                </Button>
                {hasUnsavedChanges && (
                  <Button 
                    type="button" 
                    onClick={saveWorkflowChanges}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Save Changes
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* JSON Dialog */}
          <Dialog open={jsonDialogOpen} onOpenChange={setJsonDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Workflows JSON</DialogTitle>
                <DialogDescription>
                  Current workflows configuration in JSON format
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(generateWorkflowJSON(), null, 2)}
                  </pre>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setJsonDialogOpen(false)}>
                  Close
                </Button>
                <Button onClick={downloadWorkflowJSON}>
                  <Download className="h-4 w-4 mr-2" />
                  Download JSON
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>


        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default Workflows;
